import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of, throwError } from 'rxjs';
import { PropertySubject } from '../../../subject/property.subject';
import { SegmentSubjectService } from '../../../subject/segment.subject';
import { SegmentInformationDto } from '../../../dto/segment.information.dto';
import { FlowMode } from '../../../dto/enum/flow.mode.enum';
import { PublishFlowFacade } from '../../../facades/publish-flow.facade';

@Component({
    selector: 'app-property-nickname',
    templateUrl: './property-nickname.component.html',
    styleUrls: ['./property-nickname.component.scss'],
})
export class PropertyNicknameComponent implements OnInit {
    private readonly step = 'nickname';
    mode: FlowMode = FlowMode.CREATE;
    nicknameForm!: FormGroup;
    propertySegment: SegmentInformationDto | null = null;
    propertySelected = '';
    isNotDataValid = false;
    isEditMode = false;
    nextUrl = '';

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly segmentSubject: SegmentSubjectService,
        private readonly propertySubject: PropertySubject,
        private readonly flow: PublishFlowFacade,
    ) {}

    ngOnInit(): void {
        this.flow.initStep(this.step);

        this.propertySegment = this.segmentSubject.getSelectedSegment();
        if (!this.hasValidSegment(this.propertySegment)) {
            this.flow.goTo('/segments');
            return;
        }

        const mode = this.flow.mode;
        if (!mode) {
            this.flow.goTo('/list-pending');
            return;
        }

        this.mode = mode;
        this.isEditMode = this.flow.isEditMode;
        this.nextUrl = this.flow.nextUrl(this.step);
        this.propertySelected = this.propertySegment!.segmentLabel;

        const cachedName = this.flow.getStepData<string | null>(this.step);
        this.initForm(cachedName);
    }

    private hasValidSegment(segment: SegmentInformationDto | null): boolean {
        return !!segment && !!segment.segmentId;
    }

    private initForm(initialName: string | null): void {
        this.nicknameForm = this.formBuilder.group({
            name: [
                initialName || '',
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(100),
                    Validators.pattern(/^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s.,'-]*$/),
                ],
            ],
        });
    }

    public goToNextPage(): Observable<string> {
        if (this.nicknameForm.invalid) {
            this.isNotDataValid = true;
            return of('');
        }
        const name = this.nicknameForm.get('name')?.value?.trim();
        if (!name) {
            this.isNotDataValid = true;
            return throwError(() => new Error('El nombre no puede estar vacío.'));
        }
        this.propertySubject.setPropertyName(name);
        this.flow.saveAndNext(this.step, name);
        return of(name);
    }

    public getValidationMessages(controlName: string): string[] {
        const control = this.nicknameForm.get(controlName);
        if (!control || !control.errors || !control.touched) {
            return [];
        }
        const errors = control.errors;
        const field = 'nombre del inmueble';
        const messages: string[] = [];
        if (errors['required']) messages.push(`El campo ${field} es obligatorio.`);
        if (errors['minlength']) messages.push(`Debe tener al menos ${errors['minlength'].requiredLength} caracteres.`);
        if (errors['maxlength']) messages.push(`No debe superar los ${errors['maxlength'].requiredLength} caracteres.`);
        if (errors['pattern']) messages.push(`Contiene caracteres no válidos.`);
        return messages;
    }

    get nameControl(): AbstractControl | null {
        return this.nicknameForm.get('name');
    }

    handlePreviousStep(): void {
        const name = this.nicknameForm.get('name')?.value?.trim();
        this.propertySubject.setPropertyName(name);
        this.flow.goTo('/segments');
    }

    handleNextStep(): void {
        if (this.nicknameForm.invalid) {
            this.isNotDataValid = true;
            return;
        }
        const name = this.nicknameForm.get('name')?.value?.trim();
        this.propertySubject.setPropertyName(name);
        this.flow.saveAndNext(this.step, name);
    }
}
